import { motion } from 'motion/react';
import { t, asArray } from '../utils/translations';

/**
 * YGGDRASIL drawn as the world tree, inside a woven ring.
 *   roots  = what is frozen before running (declaration + data)
 *   trunk  = the integrity gate
 *   crown  = the eight test blocks
 *   heart  = the verdict
 *   ring   = the ledger that counts every trial
 * Pure inline SVG, animated with CSS only. `compact` drops every label (used on the home page).
 * Norse dressing: an interlaced knot band, the Elder Futhark around the rim, a valknut among the roots.
 */

const ACCENT = '#00ffc8';
const GOLD = '#F4C95D';
const BG = '#06060e';
const MONO = "'JetBrains Mono', monospace";
const RUNIC = "'Cinzel', 'Bebas Neue', serif";

const CX = 450;
const CY = 450;
const RIM_R = 412; // outer line of the ring
const BAND_R = 392; // centre of the woven band
const BAND_AMP = 8;
const BAND_K = 20; // periods of the weave
const INNER_R = 372; // inner line of the ring
const RUNE_R = 431; // the futhark sits outside the rim
const HEART = { x: 450, y: 400, r: 58 };
const BASE = { x: 450, y: 590 };
const LEAF = { w: 124, h: 44 };
const CYCLE = 4.8; // seconds, one full "audit" of the tree

// Eight leaf clusters over the crown, from left to right. Alternating radii so neighbours never touch.
const LEAF_RADII = [232, 275, 242, 282, 282, 242, 275, 232];
const X_SCALE = 1.05;

function leafCenter(i) {
  const a = ((164 + (i + 0.5) * 26.5) * Math.PI) / 180;
  const r = LEAF_RADII[i];
  return { x: HEART.x + r * Math.cos(a) * X_SCALE, y: HEART.y + r * Math.sin(a), a };
}

function bezier(p0, p1, p2, p3, u) {
  const v = 1 - u;
  return {
    x: v * v * v * p0.x + 3 * v * v * u * p1.x + 3 * v * u * u * p2.x + u * u * u * p3.x,
    y: v * v * v * p0.y + 3 * v * v * u * p1.y + 3 * v * u * u * p2.y + u * u * u * p3.y,
  };
}

function bezierTangent(p0, p1, p2, p3, u) {
  const v = 1 - u;
  const x = 3 * v * v * (p1.x - p0.x) + 6 * v * u * (p2.x - p1.x) + 3 * u * u * (p3.x - p2.x);
  const y = 3 * v * v * (p1.y - p0.y) + 6 * v * u * (p2.y - p1.y) + 3 * u * u * (p3.y - p2.y);
  const n = Math.hypot(x, y) || 1;
  return { x: x / n, y: y / n };
}

function branchTo(leaf) {
  const s = { x: HEART.x + HEART.r * Math.cos(leaf.a), y: HEART.y + HEART.r * Math.sin(leaf.a) };
  const c1 = { x: s.x + (leaf.x - s.x) * 0.15, y: s.y + (leaf.y - s.y) * 0.65 };
  const c2 = { x: s.x + (leaf.x - s.x) * 0.65, y: leaf.y + (s.y - leaf.y) * 0.12 };
  return { s, c1, c2, e: leaf, d: `M${s.x.toFixed(1)} ${s.y.toFixed(1)} C ${c1.x.toFixed(1)} ${c1.y.toFixed(1)}, ${c2.x.toFixed(1)} ${c2.y.toFixed(1)}, ${leaf.x.toFixed(1)} ${leaf.y.toFixed(1)}` };
}

// A closed wave around the ring; two of them, half a period apart, weave over and under each other.
function wovenPath(phase) {
  const N = 720;
  let d = '';
  for (let i = 0; i <= N; i++) {
    const th = (i / N) * 2 * Math.PI;
    const r = BAND_R + BAND_AMP * Math.sin(BAND_K * th + phase);
    d += `${i ? 'L' : 'M'}${(CX + r * Math.cos(th)).toFixed(1)} ${(CY + r * Math.sin(th)).toFixed(1)} `;
  }
  return d + 'Z';
}
const WEAVE_A = wovenPath(0);
const WEAVE_B = wovenPath(Math.PI);

// Elder Futhark, as strokes in a 10×16 box, so no font is needed.
export const RUNES = [
  'M2 16 L2 0 M2 4 L8 1 M2 8 L8 5', // fehu
  'M2 16 L2 0 L8 4 L8 16', // uruz
  'M2 16 L2 0 M2 4 L8 8 L2 12', // thurisaz
  'M2 16 L2 0 M2 2 L8 5 M2 6 L8 9', // ansuz
  'M2 16 L2 0 L8 3 L2 7 L8 16', // raido
  'M8 0 L2 8 L8 16', // kenaz
  'M1 0 L9 16 M9 0 L1 16', // gebo
  'M2 16 L2 0 L8 3 L2 7', // wunjo
  'M2 0 L2 16 M8 0 L8 16 M2 5 L8 11', // hagalaz
  'M5 0 L5 16 M1 5 L9 11', // naudiz
  'M5 0 L5 16', // isa
  'M3 3 L7 6 L3 9 M7 7 L3 10 L7 13', // jera
  'M2 3 L5 0 L5 16 L8 13', // eihwaz
  'M2 16 L2 0 L6 3 L2 7 M2 9 L6 13 L2 16', // perthro
  'M5 16 L5 4 M5 4 L1 0 M5 4 L9 0', // algiz
  'M8 0 L2 6 L8 10 L2 16', // sowilo
  'M5 16 L5 0 M5 0 L1 4 M5 0 L9 4', // tiwaz
  'M2 16 L2 0 L7 3 L2 7 L7 11 L2 16', // berkano
  'M2 16 L2 0 L5 4 L8 0 L8 16', // ehwaz
  'M2 16 L2 0 L8 6 M8 0 L2 6 M8 0 L8 16', // mannaz
  'M3 16 L3 0 L8 4', // laguz
  'M5 2 L9 8 L5 14 L1 8 Z', // ingwaz
  'M1 0 L1 16 L9 0 L9 16 Z', // dagaz
  'M5 0 L9 6 L2 16 M5 0 L1 6 L8 16', // othala
];

const ROOT_L = { x: 285, y: 672 };
const ROOT_R = { x: 615, y: 672 };
const ROOT_NODE = { w: 170, h: 56 };

// Drawn from the node towards the base so the pulse climbs.
const rootPaths = [
  `M${ROOT_L.x} ${ROOT_L.y - ROOT_NODE.h / 2} C 320 600, 400 615, ${BASE.x - 12} ${BASE.y - 2}`,
  `M${ROOT_R.x} ${ROOT_R.y - ROOT_NODE.h / 2} C 580 600, 500 615, ${BASE.x + 12} ${BASE.y - 2}`,
];
const tendrils = [
  'M446 590 C 430 650, 360 700, 300 770',
  'M454 590 C 470 650, 540 700, 600 770',
  'M442 590 C 400 630, 260 650, 185 690',
  'M458 590 C 500 630, 640 650, 715 690',
  'M450 592 C 452 640, 448 680, 450 706',
  'M448 590 C 420 680, 385 740, 385 800',
  'M452 590 C 480 680, 515 740, 515 800',
];

// Valknut: three interlocked triangles among the roots.
function triangle(cx, baseY, s) {
  const h = 0.866 * s;
  return `M${cx} ${baseY - h} L${cx + s / 2} ${baseY} L${cx - s / 2} ${baseY} Z`;
}
const VALKNUT = [triangle(450, 752, 40), triangle(440, 769, 40), triangle(460, 769, 40)];

const trunkPath = `M${BASE.x - 44} ${BASE.y + 4} C ${BASE.x - 30} 545, ${BASE.x - 26} 500, ${BASE.x - 22} ${HEART.y + HEART.r - 6} L ${HEART.x + 22} ${HEART.y + HEART.r - 6} C ${BASE.x + 26} 500, ${BASE.x + 30} 545, ${BASE.x + 44} ${BASE.y + 4} Z`;
const trunkSpine = `M${BASE.x} ${BASE.y} L ${HEART.x} ${HEART.y + HEART.r}`;
const barkLines = [
  'M438 586 C 434 545, 436 505, 441 462',
  'M462 586 C 466 545, 464 505, 459 462',
  'M450 582 C 448 552, 452 522, 450 476',
];

const LEAF_SHAPE = `M${-LEAF.w / 2} 0 C ${-LEAF.w * 0.24} ${-LEAF.h * 0.6}, ${LEAF.w * 0.24} ${-LEAF.h * 0.6}, ${LEAF.w / 2} 0 C ${LEAF.w * 0.24} ${LEAF.h * 0.6}, ${-LEAF.w * 0.24} ${LEAF.h * 0.6}, ${-LEAF.w / 2} 0 Z`;

export default function YggdrasilTree({ language, compact = false, style, fruits = [], onFruit, fruitOpacity }) {
  const Y = (k) => t(`yggdrasil.diagram.${k}`, language);
  const blocks = compact ? [] : asArray(t('yggdrasil.diagram.blockList', language));
  const leaves = Array.from({ length: 8 }, (_, i) => leafCenter(i));
  const branches = leaves.map(branchTo);
  const gateNote = compact ? [] : String(Y('gateNote')).split(/:\s*/);

  const rootDelay = 0;
  const trunkDelay = 0.75;
  const gateDelay = 0.95;
  const branchDelay = (i) => 1.45 + i * 0.07;
  const leafDelay = (i) => 2.05 + i * 0.09;
  const heartDelay = 3.0;

  return (
    <svg
      viewBox="0 0 900 900"
      width="100%"
      style={{ display: 'block', ...style }}
      role="img"
      aria-label={compact ? 'YGGDRASIL' : Y('aria')}
    >
      <defs>
        <radialGradient id="ygg-heart-fill" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={GOLD} stopOpacity="0.28" />
          <stop offset="70%" stopColor={ACCENT} stopOpacity="0.07" />
          <stop offset="100%" stopColor={ACCENT} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="ygg-trunk-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,255,200,0.22)" />
          <stop offset="100%" stopColor="rgba(0,255,200,0.08)" />
        </linearGradient>
        <path id="ygg-ring-text-path" d={`M ${CX - 364} ${CY} A 364 364 0 0 1 ${CX + 364} ${CY}`} />
        <style>{`
          .ygg-pulse { fill: none; stroke: ${ACCENT}; stroke-linecap: round; stroke-dasharray: 18 100;
            stroke-dashoffset: 18; animation: ygg-run ${CYCLE}s linear infinite; }
          @keyframes ygg-run { 0% { stroke-dashoffset: 18; opacity: 1; } 14% { stroke-dashoffset: -100; opacity: 1; } 14.1% { opacity: 0; } 100% { stroke-dashoffset: -100; opacity: 0; } }
          .ygg-glow { animation: ygg-glow ${CYCLE}s ease-in-out infinite; opacity: 0.72; }
          @keyframes ygg-glow { 0% { opacity: 0.72; } 6% { opacity: 1; } 22% { opacity: 0.72; } 100% { opacity: 0.72; } }
          .ygg-gate { animation: ygg-gate ${CYCLE}s ease-in-out infinite; }
          @keyframes ygg-gate { 0% { opacity: 0.55; } 4% { opacity: 1; } 12% { opacity: 0.55; } 100% { opacity: 0.55; } }
          .ygg-heart { transform-box: fill-box; transform-origin: center; animation: ygg-heart ${CYCLE}s ease-in-out infinite; }
          @keyframes ygg-heart { 0% { opacity: 0.55; transform: scale(1); } 8% { opacity: 1; transform: scale(1.08); } 24% { opacity: 0.6; transform: scale(1); } 100% { opacity: 0.55; transform: scale(1); } }
          .ygg-comet { fill: none; stroke: ${GOLD}; stroke-width: 2.5; stroke-linecap: round; stroke-dasharray: 40 1000;
            animation: ygg-orbit 14s linear infinite; opacity: 0.9; }
          @keyframes ygg-orbit { from { stroke-dashoffset: 1040; } to { stroke-dashoffset: 0; } }
          .ygg-runes { transform-box: fill-box; transform-origin: center; animation: ygg-spin 240s linear infinite; }
          @keyframes ygg-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .ygg-fruit { animation: ygg-glow ${CYCLE}s ease-in-out infinite; opacity: 0.8; }
          .ygg-fruit:hover path { fill: ${GOLD}; }
          .ygg-fruit:hover path + path { fill: ${BG}; }
          @media (prefers-reduced-motion: reduce) {
            .ygg-pulse, .ygg-comet { animation: none; opacity: 0; }
            .ygg-glow, .ygg-gate, .ygg-heart, .ygg-runes, .ygg-fruit { animation: none; opacity: 0.9; }
          }
        `}</style>
      </defs>

      {/* rim, runes and the woven band = the ledger */}
      <circle cx={CX} cy={CY} r={RIM_R} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" />
      <circle cx={CX} cy={CY} r={INNER_R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      <circle cx={CX} cy={CY} r={BAND_R} fill="none" stroke="rgba(0,255,200,0.05)" strokeWidth={BAND_AMP * 2 + 6} />
      <g className="ygg-runes" opacity="0.55">
        {RUNES.map((d, i) => {
          const th = (i / RUNES.length) * 360;
          const x = CX + RUNE_R * Math.cos((th * Math.PI) / 180);
          const y = CY + RUNE_R * Math.sin((th * Math.PI) / 180);
          return (
            <path key={i} d={d} fill="none" stroke={GOLD} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
              transform={`translate(${x.toFixed(1)} ${y.toFixed(1)}) rotate(${(th + 90).toFixed(1)}) translate(-5 -8)`} />
          );
        })}
      </g>
      <g fill="none" strokeWidth="2" strokeLinejoin="round">
        <path d={WEAVE_A} stroke={ACCENT} strokeOpacity="0.5" />
        <path d={WEAVE_B} stroke={GOLD} strokeOpacity="0.5" />
        {/* over-crossings: a dark cut under each visible segment makes the other strand pass beneath */}
        <path d={WEAVE_A} pathLength={2 * BAND_K} stroke={BG} strokeWidth="7" strokeDasharray="1 1" strokeDashoffset="0.5" />
        <path d={WEAVE_A} pathLength={2 * BAND_K} stroke={ACCENT} strokeOpacity="0.5" strokeDasharray="1 1" strokeDashoffset="0.5" />
        <path d={WEAVE_B} pathLength={2 * BAND_K} stroke={BG} strokeWidth="7" strokeDasharray="1 1" strokeDashoffset="-0.5" />
        <path d={WEAVE_B} pathLength={2 * BAND_K} stroke={GOLD} strokeOpacity="0.5" strokeDasharray="1 1" strokeDashoffset="-0.5" />
      </g>
      <circle className="ygg-comet" cx={CX} cy={CY} r={RIM_R} pathLength="1000" />
      {!compact && (
        <text fill="#8a8a8a" fontFamily={MONO} fontSize="11" letterSpacing="2.5">
          <textPath href="#ygg-ring-text-path" startOffset="50%" textAnchor="middle">{Y('ledgerText')}</textPath>
        </text>
      )}

      {/* roots and the valknut */}
      {tendrils.map((d, i) => (
        <path key={i} d={d} fill="none" stroke="rgba(0,255,200,0.24)" strokeWidth={i < 2 ? 3 : 1.8} strokeLinecap="round" />
      ))}
      <g fill="none" stroke={GOLD} strokeOpacity="0.55" strokeWidth="1.5" strokeLinejoin="round">
        {VALKNUT.map((d, i) => <path key={i} d={d} />)}
      </g>
      {rootPaths.map((d, i) => (
        <g key={i}>
          <path d={d} fill="none" stroke="rgba(0,255,200,0.5)" strokeWidth="4.5" strokeLinecap="round" />
          <path className="ygg-pulse" d={d} pathLength="100" strokeWidth="4" style={{ animationDelay: `${rootDelay + i * 0.05}s` }} />
        </g>
      ))}
      {[ROOT_L, ROOT_R].map((n, i) => {
        const kicker = i === 0 ? Y('preregKicker') : Y('dataKicker');
        const title = i === 0 ? Y('preregTitle') : Y('dataTitle');
        const sub = i === 0 ? Y('preregSub') : Y('dataSub');
        if (compact) {
          return <circle key={i} cx={n.x} cy={n.y - 22} r="9" fill="#0b1010" stroke="#A78BFA" strokeOpacity="0.8" strokeWidth="2" />;
        }
        return (
          <g key={i}>
            <rect x={n.x - ROOT_NODE.w / 2} y={n.y - ROOT_NODE.h / 2} width={ROOT_NODE.w} height={ROOT_NODE.h} rx="6" fill="#0b1010" stroke="#A78BFA" strokeOpacity="0.6" strokeWidth="1.5" />
            <rect x={n.x - ROOT_NODE.w / 2 + 4} y={n.y - ROOT_NODE.h / 2 + 4} width={ROOT_NODE.w - 8} height={ROOT_NODE.h - 8} rx="4" fill="none" stroke="#A78BFA" strokeOpacity="0.25" strokeWidth="1" />
            <text x={n.x} y={n.y - 9} textAnchor="middle" fill="#A78BFA" fontFamily={MONO} fontSize="8.5" letterSpacing="1.5">{kicker}</text>
            <text x={n.x} y={n.y + 8} textAnchor="middle" fill="#fff" fontFamily={RUNIC} fontSize="14" fontWeight="600" letterSpacing="1">{title}</text>
            <text x={n.x} y={n.y + 21} textAnchor="middle" fill="#8a8a8a" fontFamily={MONO} fontSize="8">{sub}</text>
          </g>
        );
      })}

      {/* trunk = integrity gate */}
      <path d={trunkPath} fill="url(#ygg-trunk-fill)" stroke="rgba(0,255,200,0.7)" strokeWidth="2" />
      {barkLines.map((d, i) => <path key={i} d={d} fill="none" stroke="rgba(0,255,200,0.28)" strokeWidth="1" />)}
      <path className="ygg-pulse" d={trunkSpine} pathLength="100" strokeWidth="6" style={{ animationDelay: `${trunkDelay}s` }} />
      {compact ? (
        <g className="ygg-gate" style={{ animationDelay: `${gateDelay}s` }}>
          <circle cx={BASE.x} cy="516" r="16" fill="#0b1010" stroke="#FF8A65" strokeWidth="2" />
          <circle cx={BASE.x} cy="516" r="5" fill="#FF8A65" />
        </g>
      ) : (
        <g className="ygg-gate" style={{ animationDelay: `${gateDelay}s` }}>
          <rect x={BASE.x - 76} y="497" width="152" height="46" rx="6" fill="#0b1010" stroke="#FF8A65" strokeOpacity="0.85" strokeWidth="1.5" />
          <text x={BASE.x} y="513" textAnchor="middle" fill="#FF8A65" fontFamily={MONO} fontSize="8.5" letterSpacing="1.5">{Y('integrityKicker')}</text>
          <text x={BASE.x} y="533" textAnchor="middle" fill="#fff" fontFamily={RUNIC} fontSize="15" fontWeight="600" letterSpacing="1.5">{Y('integrityTitle')}</text>
          {gateNote.map((line, i) => (
            <text key={i} x={BASE.x + 88} y={512 + i * 14} fill="#FF8A65" fontFamily={MONO} fontSize="9" opacity="0.9">{line}{i === 0 && gateNote.length > 1 ? ':' : ''}</text>
          ))}
        </g>
      )}

      {/* branches + small leaves */}
      {branches.map((b, i) => (
        <g key={i}>
          <path d={b.d} fill="none" stroke="rgba(0,255,200,0.5)" strokeWidth={3.4} strokeLinecap="round" />
          <path className="ygg-pulse" d={b.d} pathLength="100" strokeWidth="3.2" style={{ animationDelay: `${branchDelay(i)}s` }} />
          {[0.36, 0.52, 0.68, 0.84].map((u, k) => {
            const p = bezier(b.s, b.c1, b.c2, b.e, u);
            const tg = bezierTangent(b.s, b.c1, b.c2, b.e, u);
            const side = k % 2 === 0 ? 1 : -1;
            const nx = -tg.y * side;
            const ny = tg.x * side;
            const lx = p.x + nx * 11;
            const ly = p.y + ny * 11;
            const rot = (Math.atan2(tg.y, tg.x) * 180) / Math.PI + side * 42;
            return (
              <g key={k} className="ygg-glow" style={{ animationDelay: `${leafDelay(i) + k * 0.05}s` }}>
                <line x1={p.x.toFixed(1)} y1={p.y.toFixed(1)} x2={lx.toFixed(1)} y2={ly.toFixed(1)} stroke={ACCENT} strokeOpacity="0.6" strokeWidth="1" />
                <ellipse cx={lx.toFixed(1)} cy={ly.toFixed(1)} rx="9" ry="4.2" fill={ACCENT} transform={`rotate(${rot.toFixed(1)} ${lx.toFixed(1)} ${ly.toFixed(1)})`} />
              </g>
            );
          })}
        </g>
      ))}

      {/* leaf clusters = the eight blocks */}
      {leaves.map((l, i) => {
        if (compact) {
          const rot = (l.a * 180) / Math.PI + 90;
          return (
            <g key={i} className="ygg-glow" style={{ animationDelay: `${leafDelay(i)}s` }}>
              <ellipse cx={l.x} cy={l.y} rx="26" ry="12" fill={ACCENT} opacity="0.85" transform={`rotate(${rot.toFixed(1)} ${l.x.toFixed(1)} ${l.y.toFixed(1)})`} />
              <ellipse cx={l.x - 18} cy={l.y + 14} rx="16" ry="7" fill={ACCENT} opacity="0.55" transform={`rotate(${(rot + 40).toFixed(1)} ${(l.x - 18).toFixed(1)} ${(l.y + 14).toFixed(1)})`} />
              <ellipse cx={l.x + 18} cy={l.y + 12} rx="16" ry="7" fill={ACCENT} opacity="0.55" transform={`rotate(${(rot - 40).toFixed(1)} ${(l.x + 18).toFixed(1)} ${(l.y + 12).toFixed(1)})`} />
            </g>
          );
        }
        const [title, hint] = String(blocks[i] || '').split('|');
        return (
          <g key={i} className="ygg-glow" style={{ animationDelay: `${leafDelay(i)}s` }} transform={`translate(${l.x.toFixed(1)} ${l.y.toFixed(1)})`}>
            <path d={LEAF_SHAPE} fill="#0b1412" stroke={ACCENT} strokeOpacity="0.75" strokeWidth="1.5" />
            <line x1={-LEAF.w / 2 + 8} y1="0" x2={-LEAF.w / 2 + 16} y2="0" stroke={ACCENT} strokeOpacity="0.5" strokeWidth="1" />
            <line x1={LEAF.w / 2 - 16} y1="0" x2={LEAF.w / 2 - 8} y2="0" stroke={ACCENT} strokeOpacity="0.5" strokeWidth="1" />
            <text x="0" y="-2" textAnchor="middle" fill="#eaeaea" fontFamily={MONO} fontSize="10.5" fontWeight="600">{title}</text>
            <text x="0" y="12" textAnchor="middle" fill="#7a8a86" fontFamily={MONO} fontSize="8.5">{hint || ''}</text>
          </g>
        );
      })}

      {/* fruits = the strategies that hang from the tree on a thread: gold amulets */}
      <motion.g style={{ opacity: fruitOpacity === undefined ? 1 : fruitOpacity }}>
      {fruits.map((f, i) => {
        // the first eight hang one per branch, far out; the rest sit closer to the heart on alternate branches
        const extra = [1, 3, 4, 6, 0, 7, 2, 5];
        const first = i < branches.length;
        const b = branches[first ? i : extra[(i - branches.length) % extra.length]];
        const u = first ? 0.6 : 0.82;
        const p = bezier(b.s, b.c1, b.c2, b.e, u);
        const drop = compact ? 34 : 26;
        const k = compact ? 1.5 : 1.1;
        const fx = p.x;
        const fy = p.y + drop;
        return (
          <g key={f.slug} className="ygg-fruit" style={{ animationDelay: `${leafDelay(i % 8) + 0.3}s`, cursor: onFruit ? 'pointer' : 'default' }}
            onClick={onFruit ? () => onFruit(f.slug) : undefined} role={onFruit ? 'link' : undefined} tabIndex={onFruit ? 0 : undefined}
            onKeyDown={onFruit ? (e) => { if (e.key === 'Enter') onFruit(f.slug); } : undefined}>
            <title>{f.name}</title>
            <line x1={fx.toFixed(1)} y1={p.y.toFixed(1)} x2={fx.toFixed(1)} y2={(fy - 11 * k).toFixed(1)} stroke={GOLD} strokeOpacity="0.55" strokeWidth="1" />
            <g transform={`translate(${fx.toFixed(1)} ${fy.toFixed(1)}) scale(${k})`}>
              <path d="M0 -11 C 7 -5, 7 6, 0 12 C -7 6, -7 -5, 0 -11 Z" fill={BG} stroke={GOLD} strokeWidth="1.4" />
              <path d="M0 -6 C 3.5 -2, 3.5 4, 0 7 C -3.5 4, -3.5 -2, 0 -6 Z" fill={GOLD} fillOpacity="0.55" />
              <line x1="-3" y1="-11" x2="3" y2="-11" stroke={GOLD} strokeWidth="1.4" strokeLinecap="round" />
            </g>
          </g>
        );
      })}
      </motion.g>

      {/* heart = verdict */}
      <circle cx={HEART.x} cy={HEART.y} r={HEART.r + 34} fill="url(#ygg-heart-fill)" />
      <g className="ygg-heart" style={{ animationDelay: `${heartDelay}s` }}>
        <circle cx={HEART.x} cy={HEART.y} r={HEART.r} fill="#0b1412" stroke={GOLD} strokeWidth="2" />
        <circle cx={HEART.x} cy={HEART.y} r={HEART.r - 7} fill="none" stroke={GOLD} strokeOpacity="0.4" strokeWidth="1" />
        {Array.from({ length: 8 }, (_, k) => {
          const a = (k * Math.PI) / 4;
          return (
            <line key={k}
              x1={(HEART.x + (HEART.r - 7) * Math.cos(a)).toFixed(1)} y1={(HEART.y + (HEART.r - 7) * Math.sin(a)).toFixed(1)}
              x2={(HEART.x + (HEART.r - 1) * Math.cos(a)).toFixed(1)} y2={(HEART.y + (HEART.r - 1) * Math.sin(a)).toFixed(1)}
              stroke={GOLD} strokeOpacity="0.7" strokeWidth="1.5" />
          );
        })}
        {compact ? (
          <text x={HEART.x} y={HEART.y + 10} textAnchor="middle" fill={GOLD} fontFamily={RUNIC} fontSize="28" fontWeight="600" letterSpacing="1">56</text>
        ) : (
          <>
            <text x={HEART.x} y={HEART.y + 3} textAnchor="middle" fill={GOLD} fontFamily={RUNIC} fontSize="13" fontWeight="600" letterSpacing="1.5">{Y('verdictTitle')}</text>
            <text x={HEART.x} y={HEART.y + 18} textAnchor="middle" fill="#9a9a9a" fontFamily={MONO} fontSize="8.5">{Y('verdictSub')}</text>
          </>
        )}
      </g>
    </svg>
  );
}
